import { Controller } from "@hotwired/stimulus"

// Populates the MOET note textarea with pre-filled email/SMS templates.
// Button clicks insert rendered template text under "### Email/SMS Content (if any):"
// and copy it to the clipboard so it can be pasted straight into Front.
export default class extends Controller {
  static values = {
    memberFirstname: String,
    subscriberFirstname: String,
    isMinor: Boolean,
    callerFirstname: String,
    callerPhone: String,
    employerName: String,
    hospital: String,
    partnerSlug: String,
    signUpCode: String,
    memberBalance: String,
    discountTable: String,
    discountThreshold: String,
    partnerName: String,
    hhSize: String,
    isPostPayment: Boolean
  }

  // When the patient is a minor, outreach is addressed to the subscriber and
  // the patient is referred to in the third person.
  get greetingName() {
    if (this.isMinorValue) {
      return this.subscriberFirstnameValue || "[Subscriber Firstname]"
    }
    return this.memberFirstnameValue
  }

  // Possessive used before "recent visit"/"bill": "your" for the patient
  // themselves, or "Marquise's" when writing to a minor's subscriber.
  get patientPossessive() {
    return this.isMinorValue ? `${this.memberFirstnameValue}'s` : "your"
  }

  insertTemplate(event) {
    event.preventDefault()
    const templateKey = event.currentTarget.dataset.moetTemplateKey
    const content = this.buildTemplate(templateKey)
    if (!content) return

    this.copyToClipboard(content, event.currentTarget)

    const textarea = document.querySelector("textarea[name='claim_note[message]']")
    if (!textarea) return
    const currentText = textarea.value
    const marker = "### Email/SMS Content (if any):"
    const markerIndex = currentText.indexOf(marker)

    if (markerIndex !== -1) {
      const beforeMarker = currentText.substring(0, markerIndex + marker.length)
      const afterMarkerText = currentText.substring(markerIndex + marker.length)
      const nextSection = afterMarkerText.indexOf("###")
      let afterContent
      if (nextSection !== -1) {
        afterContent = afterMarkerText.substring(nextSection)
      } else {
        afterContent = ""
      }
      textarea.value = beforeMarker + "\n" + content + "\n\n" + afterContent
    } else {
      textarea.value = currentText + "\n\n" + content
    }

    textarea.dispatchEvent(new Event("change", { bubbles: true }))
  }

  copyToClipboard(text, button) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => this.showCopied(button))
        .catch(() => this.fallbackCopy(text, button))
    } else {
      this.fallbackCopy(text, button)
    }
  }

  fallbackCopy(text, button) {
    const textArea = document.createElement("textarea")
    textArea.value = text
    textArea.style.position = "fixed"
    textArea.style.left = "-999999px"
    textArea.style.top = "-999999px"
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      // execCommand signals a blocked or unsupported copy by returning false
      // rather than throwing, so don't claim success on it.
      if (document.execCommand("copy")) {
        this.showCopied(button)
      }
    } catch (err) {
      // Copy failed silently
    }

    document.body.removeChild(textArea)
  }

  showCopied(button) {
    if (!button) return
    if (button.dataset.originalLabel === undefined) {
      button.dataset.originalLabel = button.textContent
    }
    button.textContent = `${button.dataset.originalLabel} \u2713 Copied`
    this.copiedTimeouts ||= new Map()
    clearTimeout(this.copiedTimeouts.get(button))
    this.copiedTimeouts.set(button, setTimeout(() => {
      button.textContent = button.dataset.originalLabel
    }, 2000))
  }

  buildTemplate(key) {
    const templates = {
      email1: this.email1Template(),
      email2: this.email2Template(),
      email3: this.email3Template(),
      sms1: this.sms1Template(),
      sms2: this.sms2Template()
    }
    return templates[key] || null
  }

  email1Template() {
    const slug = this.partnerSlugValue
    if (slug === "clearwater") {
      return this.email1ClearwaterTemplate()
    } else if (slug === "netwell") {
      return this.email1NetwellTemplate()
    }
    return this.email1GenericTemplate()
  }

  email1GenericTemplate() {
    return `Subject: Following up on my voicemail

Hi ${this.greetingName},

It's ${this.callerFirstnameValue} from Goodbill \u2014 I just left you a voicemail.

I'm reaching out because we're included in your ${this.employerNameValue} health plan to help you save on hospital visits, including ${this.patientPossessive} recent visit to ${this.hospitalValue}.

As mentioned in my voicemail, this service is paid by your health plan and completely free to you. To move forward, we just need to check your eligibility for the hospital's discount program:

\u2705 Completed: We've received your claim from ${this.hospitalValue}
\uD83D\uDC49 Next: Check your discount eligibility at goodbill.com/${this.partnerSlugValue} using your secure sign-in code: ${this.signUpCodeValue}

${this.hospitalValue} offers discounts that could reduce \u2014 or even 100% eliminate \u2014 your balance if you qualify. The process takes about a minute.

If you have any questions, feel free to reply to this email or call me directly.

Thanks,
${this.callerFirstnameValue}
Case Manager, Goodbill
${this.callerPhoneValue}`
  }

  email1ClearwaterTemplate() {
    return `Subject: Following up on my voicemail

Hi ${this.greetingName},

It's ${this.callerFirstnameValue} from Goodbill \u2014 I just left you a voicemail.

I'm reaching out because Goodbill is included in your health plan, administered by Clearwater, to help you save on hospital visits, including ${this.patientPossessive} recent visit to ${this.hospitalValue}.

As mentioned in my voicemail, this service is paid for by your plan and completely free to you. To move forward, we just need to check your eligibility for the hospital's discount program:

\u2705 Completed: We've received your claim from ${this.hospitalValue}
\uD83D\uDC49 Next: Check your discount eligibility at goodbill.com/clearwater using your secure sign-in code: ${this.signUpCodeValue}

${this.hospitalValue} offers discounts that could reduce \u2014 or even 100% eliminate \u2014 your balance if you qualify. The process takes about a minute.

Please note that completing this step is required by Clearwater in order to process your claim.

If you have any questions, feel free to reply to this email or call me directly.

Thanks,
${this.callerFirstnameValue}
Case Manager, Goodbill
${this.callerPhoneValue}`
  }

  email1NetwellTemplate() {
    const disclaimer = this.isPostPaymentValue
      ? ""
      : "\nPlease note that completing this step is required by netWell in order to process your claim.\n"

    return `Subject: Following up on my voicemail

Hi ${this.greetingName},

It's ${this.callerFirstnameValue} from Goodbill \u2014 I just left you a voicemail.

I'm reaching out because Goodbill is included in your health plan, administered by netWell, to help you save on hospital visits, including ${this.patientPossessive} recent visit to ${this.hospitalValue}.

As mentioned in my voicemail, this service is paid for by your plan and completely free to you. To move forward, we just need to check your eligibility for the hospital's discount program:

\u2705 Completed: We've received your claim from ${this.hospitalValue}
\uD83D\uDC49 Next: Check your discount eligibility at goodbill.com/netwell using your secure sign-in code: ${this.signUpCodeValue}

${this.hospitalValue} offers discounts that could reduce \u2014 or even 100% eliminate \u2014 your balance if you qualify. The process takes about a minute.
${disclaimer}
If you have any questions, feel free to reply to this email or call me directly.

Thanks,
${this.callerFirstnameValue}
Case Manager, Goodbill
${this.callerPhoneValue}`
  }

  email2Template() {
    return `Subject: Reminder: Next step to reduce ${this.patientPossessive} hospital bill

Hi ${this.greetingName},

I noticed the hospital discount screening hasn't been completed yet \u2014 do you have any questions I can help with?

For reference, if your household has ${this.hhSizeValue} ${this.hhSizeValue === "1" ? "person" : "people"} and your income is under ${this.discountThresholdValue}, you may qualify for a bill reduction at ${this.hospitalValue}. Goodbill handles the process for you at no cost.

\uD83D\uDC49 Check your discount eligibility: Visit goodbill.com/${this.partnerSlugValue} using your secure sign-in code: ${this.signUpCodeValue}. It takes about a minute.

Want to schedule a call to walk through the process? Just reply to this email.

Thanks,
${this.callerFirstnameValue}
Case Manager, Goodbill
${this.callerPhoneValue}`
  }

  email3Template() {
    return `Subject: Reminder: Save on ${this.patientPossessive} hospital bill

Hi ${this.greetingName},

Checking in one last time to see if you have questions about your Goodbill benefit.

As a reminder, we're embedded in your ${this.employerNameValue} health plan as a patient advocacy benefit, and we've identified a potential opportunity to save money on ${this.patientPossessive} recent visit to ${this.hospitalValue}.

\uD83D\uDC49 Check your discount eligibility: Visit goodbill.com/${this.partnerSlugValue} and enter your secure sign-in code: ${this.signUpCodeValue}

We'll plan to close out your case in a few days if we don't hear back.

Thanks,
${this.callerFirstnameValue}
Case Manager, Goodbill
${this.callerPhoneValue}`
  }

  sms1Template() {
    return `Hi ${this.greetingName}, this is ${this.callerFirstnameValue} from Goodbill. We're a benefit included in your ${this.employerNameValue} health plan to help reduce ${this.patientPossessive} ${this.memberBalanceValue} bill from ${this.hospitalValue}.

We check for discounts and review your bill for errors. Here's how to get started:

\uD83D\uDC49 Visit goodbill.com/${this.partnerSlugValue}. Use code: ${this.signUpCodeValue}

Questions? Call me at ${this.callerPhoneValue}.`
  }

  sms2Template() {
    return `Hi ${this.greetingName}! This is ${this.callerFirstnameValue} from Goodbill \u2014 we're part of your ${this.employerNameValue} health plan and we've identified an opportunity to lower the cost of ${this.patientPossessive} recent visit to ${this.hospitalValue}.

There's a short window before your bill is finalized to check for discounts and reduce what you may owe. Let's get started:

\uD83D\uDC49 Visit goodbill.com/${this.partnerSlugValue}. Use code: ${this.signUpCodeValue}

Questions? Call me at ${this.callerPhoneValue}.`
  }
}
